import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, X, Plus, Settings2, Share2 } from 'lucide-react';
import { AchievementUnlockModal } from '@/components/AchievementUnlockModal';
import { Mascot } from '@/components/Mascot';
import { useTelegram } from '@/hooks/useTelegram';
import {
  useHabits, HABIT_LIMIT, HABIT_PRESETS, MISS_REASONS, HABIT_MILESTONES, HABIT_LINES,
  type Habit, type HabitStatus,
} from '@/hooks/useHabits';

// ════════════════════════════════════════════════════════════════
// Привычки с Дофи на главной (22.09.2026, по образцу вечерних «трёх кнопок»).
// Пустое состояние — одна тихая строка-приглашение, не блок на пол-экрана.
// Отмечать можно в любой момент, но подзаголовок зовёт вечером: утром
// «получилось ли лечь до 23» ещё не про сегодня.
// На «Нет» — мягкий вопрос «что помешало?» и тёплая фраза, без укора.
// ════════════════════════════════════════════════════════════════

const STATUS_BTN: { s: HabitStatus; label: string }[] = [
  { s: 'done', label: 'Получилось' },
  { s: 'partial', label: 'Частично' },
  { s: 'missed', label: 'Нет' },
];

function dayWord(n: number) {
  return n % 10 === 1 && n % 100 !== 11 ? 'день' : n % 10 >= 2 && n % 10 <= 4 && (n % 100 < 10 || n % 100 >= 20) ? 'дня' : 'дней';
}

// Клетка полоски — те же цвета, что в сетке «Твой путь» на Статистике.
// «Нет» и пропуск выглядят одинаково тускло: без красного, без стыда.
function cellStyle(status: HabitStatus | null): React.CSSProperties {
  if (status === 'done') return { background: '#10E58E', boxShadow: '0 0 6px rgba(16,229,142,0.7)' };
  if (status === 'partial') return { background: 'rgba(16,229,142,0.34)' };
  return { background: 'rgba(255,255,255,0.06)' };
}

function HabitStrip({ days, today }: { days: { day: string; status: HabitStatus | null }[]; today: string }) {
  const kept = days.filter((d) => d.status === 'done' || d.status === 'partial').length;
  const next = HABIT_MILESTONES.find((m) => m > kept);
  return (
    <div className="mb-2.5">
      <div className="flex gap-[3px]" aria-hidden="true">
        {days.map((d) => (
          <motion.div
            key={d.day + (d.status ?? '')}
            initial={d.day === today && d.status ? { scale: 0.4, opacity: 0.4 } : false}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ type: 'spring', stiffness: 400, damping: 14 }}
            // Ширина делится поровну, но не больше 14px: на узком телефоне
            // 21 клетка влезает в карточку, на широком не растягивается.
            className={`h-3 flex-1 max-w-[14px] rounded-[3px] ${d.day === today ? 'ring-1 ring-ink-muted/60' : ''}`}
            style={cellStyle(d.status)}
          />
        ))}
      </div>
      <div className="flex items-center justify-between mt-1.5 text-[10px] text-ink-dim">
        <span>
          получилось <b className="text-ink-muted">{kept}</b> из {days.length} {dayWord(days.length)}
        </span>
        {next && kept > 0 && (
          <span>
            до {next} {dayWord(next)} — ещё {next - kept}
            {next === 66 && ' · тут привычка выходит на автопилот'}
          </span>
        )}
      </div>
    </div>
  );
}

// Веха привычки в формате окна достижений: то же окно, та же карточка
// и те же три канала шеринга, что у остальных достижений.
function habitAchievement(h: Habit, m: number) {
  return {
    id: `habit_${m}`,
    category: 'habit',
    title: h.title,
    description: '',
    icon: h.emoji,
    xp_reward: 0,
    target_value: m,
    shareable: true,
    slava_line: HABIT_LINES[m] ?? null,
  };
}

function HabitRow({
  habit, status, reason, streak, days, today, onMark, onShare,
}: {
  habit: Habit;
  status: HabitStatus | undefined;
  reason: string | null | undefined;
  streak: number;
  days: { day: string; status: HabitStatus | null }[];
  today: string;
  onMark: (s: HabitStatus, reason?: string | null) => void;
  onShare?: () => void;
}) {
  const { haptic } = useTelegram();
  return (
    <div className="py-3 border-t border-line/60 first:border-t-0">
      <div className="flex items-center gap-2 mb-2">
        <span className="text-lg leading-none">{habit.emoji}</span>
        <span className="text-sm font-medium flex-1 min-w-0">{habit.title}</span>
        {streak >= 2 && (
          <span className="text-[11px] text-emerald-glow shrink-0">🔥 {streak} {dayWord(streak)}</span>
        )}
        {onShare && (
          <button type="button" onClick={onShare} className="p-1 -mr-1 text-emerald-glow shrink-0" aria-label="Поделиться успехом">
            <Share2 size={14} />
          </button>
        )}
      </div>
      <HabitStrip days={days} today={today} />
      <div className="grid grid-cols-3 gap-1.5">
        {STATUS_BTN.map(({ s, label }) => {
          const on = status === s;
          const tone =
            s === 'missed'
              ? on ? 'bg-bg-elevated border-ink-dim text-ink' : 'border-line text-ink-muted'
              : on ? 'bg-emerald-glow/15 border-emerald-glow/60 text-emerald-glow' : 'border-line text-ink-muted';
          return (
            <button
              key={s}
              type="button"
              onClick={() => { haptic('light'); onMark(s, s === 'missed' ? reason ?? null : null); }}
              className={`py-2 rounded-xl border text-xs font-medium flex items-center justify-center gap-1 active:scale-[0.97] transition-all ${tone}`}
              aria-pressed={on}
            >
              {s === 'done' && <Check size={12} />}
              {s === 'missed' && <X size={12} />}
              {label}
            </button>
          );
        })}
      </div>

      <AnimatePresence initial={false}>
        {status === 'missed' && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden"
          >
            <div className="text-xs text-ink-muted mt-2.5 mb-1.5">
              {reason ? 'Бывает. Завтра — новая попытка, серия не главное.' : 'Бывает. Что помешало?'}
            </div>
            <div className="flex flex-wrap gap-1.5">
              {MISS_REASONS.map((r) => (
                <button
                  key={r}
                  type="button"
                  onClick={() => { haptic('light'); onMark('missed', r); }}
                  className={`px-2.5 py-1 rounded-full border text-[11px] transition-colors ${
                    reason === r ? 'border-emerald-glow/60 text-emerald-glow bg-emerald-glow/10' : 'border-line text-ink-muted'
                  }`}
                >
                  {r}
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function Picker({
  taken, left, onAdd, onClose,
}: {
  taken: string[];
  left: number;
  onAdd: (title: string, emoji: string) => Promise<boolean>;
  onClose: () => void;
}) {
  const [custom, setCustom] = useState('');
  const [busy, setBusy] = useState(false);
  const { haptic } = useTelegram();

  async function add(title: string, emoji: string) {
    if (busy || !title.trim()) return;
    setBusy(true);
    const ok = await onAdd(title, emoji);
    setBusy(false);
    if (ok) { haptic('success'); setCustom(''); if (left <= 1) onClose(); }
  }

  return (
    <div className="pt-1">
      <div className="text-xs text-ink-muted mb-2">
        Выбери до {HABIT_LIMIT} — осталось {left}. Одна-две привычки держатся лучше, чем пять.
      </div>
      <div className="flex flex-wrap gap-1.5 mb-3">
        {HABIT_PRESETS.filter((p) => !taken.includes(p.title)).map((p) => (
          <button
            key={p.title}
            type="button"
            disabled={busy || left <= 0}
            onClick={() => add(p.title, p.emoji)}
            className="px-3 py-1.5 rounded-full border border-line text-xs text-ink hover:border-emerald-glow/40 disabled:opacity-40 active:scale-[0.97] transition-all"
          >
            {p.emoji} {p.title}
          </button>
        ))}
      </div>
      <div className="flex gap-2">
        <input
          id="habit-custom"
          value={custom}
          onChange={(e) => setCustom(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') add(custom, '✨'); }}
          maxLength={80}
          placeholder="Своя привычка, например «10 000 шагов»"
          className="input text-sm flex-1 min-w-0"
          disabled={left <= 0}
        />
        <button
          type="button"
          onClick={() => add(custom, '✨')}
          disabled={busy || !custom.trim() || left <= 0}
          className="px-3 rounded-xl bg-emerald-glow text-bg text-sm font-bold disabled:opacity-40"
          aria-label="Добавить привычку"
        >
          <Plus size={16} />
        </button>
      </div>
      <button type="button" onClick={onClose} className="mt-3 text-xs text-ink-dim">Готово</button>
    </div>
  );
}

export function HabitsCard() {
  const { habits, loaded, addHabit, archiveHabit, mark, todayLog, streak, history, keptCount, today } = useHabits();
  const [celebrate, setCelebrate] = useState<ReturnType<typeof habitAchievement> | null>(null);

  // Отметка + проверка вехи: если этой отметкой человек дошёл до 7/21/30/66/100
  // дней — сразу окно с Дофи и предложением поделиться.
  function markWithMilestone(h: Habit, s: HabitStatus, r: string | null) {
    const before = keptCount(h.id);
    const wasKept = (() => { const l = todayLog(h.id); return !!l && l.status !== 'missed'; })();
    const nowKept = s !== 'missed';
    const after = before + (nowKept && !wasKept ? 1 : 0) - (!nowKept && wasKept ? 1 : 0);
    mark(h.id, s, r);
    const hit = HABIT_MILESTONES.find((m) => before < m && after >= m);
    if (hit) setTimeout(() => setCelebrate(habitAchievement(h, hit)), 450);
  }
  const [picking, setPicking] = useState(false);
  const [editing, setEditing] = useState(false);
  const { haptic } = useTelegram();

  if (!loaded) return null;

  const evening = new Date().getHours() >= 18;
  const doneCount = habits.filter((h) => { const l = todayLog(h.id); return l && l.status !== 'missed'; }).length;

  // Пусто и выбор не открыт — одна строка-приглашение.
  if (habits.length === 0 && !picking) {
    return (
      <button
        type="button"
        onClick={() => { haptic('light'); setPicking(true); }}
        className="w-full card p-3 mb-4 flex items-center gap-3 text-left active:scale-[0.99] transition-transform"
      >
        <Mascot pose="calm" compact float={false} className="w-10 h-10 shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium">Привычки с Дофи</div>
          <div className="text-xs text-ink-muted">Выбери 1–3, а вечером Дофи спросит, получилось ли</div>
        </div>
        <Plus size={18} className="text-emerald-glow shrink-0" />
      </button>
    );
  }

  return (
    <div className="card p-4 mb-4">
      <AchievementUnlockModal open={!!celebrate} achievement={celebrate} onClose={() => setCelebrate(null)} />
      <div className="flex items-center gap-2.5 mb-1">
        <Mascot pose={doneCount > 0 && doneCount === habits.length ? 'celebrate' : 'calm'} compact float={false} className="w-8 h-8 shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium">Привычки дня</div>
          <div className="text-[11px] text-ink-dim">
            {habits.length === 0
              ? 'Выбери, с чего начать'
              : doneCount === habits.length
                ? 'Все отмечены. Хороший день'
                : evening ? 'Как прошло сегодня?' : 'Отметь вечером — или сразу, если уже сделал(а)'}
          </div>
        </div>
        {habits.length > 0 && !picking && (
          <button
            type="button"
            onClick={() => setEditing((v) => !v)}
            className="p-1.5 text-ink-dim"
            aria-label="Изменить привычки"
          >
            <Settings2 size={16} />
          </button>
        )}
      </div>

      {editing ? (
        <div className="pt-2">
          {habits.map((h) => (
            <div key={h.id} className="flex items-center gap-2 py-2 border-t border-line/60 first:border-t-0">
              <span className="text-lg leading-none">{h.emoji}</span>
              <span className="text-sm flex-1 min-w-0">{h.title}</span>
              <button
                type="button"
                onClick={() => archiveHabit(h.id)}
                className="text-xs text-ink-muted px-2 py-1 rounded-lg border border-line"
              >
                Убрать
              </button>
            </div>
          ))}
          <div className="flex gap-3 mt-3">
            {habits.length < HABIT_LIMIT && (
              <button type="button" onClick={() => { setEditing(false); setPicking(true); }} className="text-xs text-emerald-glow">
                + Добавить
              </button>
            )}
            <button type="button" onClick={() => setEditing(false)} className="text-xs text-ink-dim">Готово</button>
          </div>
          <div className="text-[10px] text-ink-dim mt-2">История отметок сохраняется, даже если привычку убрать.</div>
        </div>
      ) : (
        <>
          {habits.map((h) => {
            const l = todayLog(h.id);
            return (
              <HabitRow
                key={h.id}
                habit={h}
                status={l?.status}
                reason={l?.reason}
                streak={streak(h.id)}
                days={history(h)}
                today={today}
                onMark={(s, r) => markWithMilestone(h, s, r ?? null)}
                onShare={(() => {
                  // Поделиться можно в любой момент последней взятой вехой
                  const reached = [...HABIT_MILESTONES].reverse().find((m) => keptCount(h.id) >= m);
                  return reached ? () => setCelebrate(habitAchievement(h, reached)) : undefined;
                })()}
              />
            );
          })}
          {picking && (
            <Picker
              taken={habits.map((h) => h.title)}
              left={HABIT_LIMIT - habits.length}
              onAdd={addHabit}
              onClose={() => setPicking(false)}
            />
          )}
        </>
      )}
    </div>
  );
}
